/**
 * This file contains all operations regarding filters.
 *
 * All possible filters are listed in filterProperties(). The defaultFilterValues() function sets the default filter
 * values. filtersToApiFilters() converts URL filters to the filters used by the comparison API.
 */

/**
 * This array contains all possible filter properties. It is used to create a filter array and to read filters from URLs
 * @returns {string[]}
 */
export function filterProperties() {
    return [
        // Package filters:
        'productType', // Product type (i, ip, it, ipt, all)
        'providers', // Array with slugs of providers
        'combiProviders', // Array with combi provider slugs
        'combiProvidersEnergy', // Array with combi provider slugs
        'deliveryTime', // Max delivery time in days
        'sort', // Sorting
        'premium', // Premium provider

        // Internet filters:
        'minSpeed', // Min download speed (package) in MBit/s
        'minUpload', // Min upload speed (package) in MBit/s
        'dsl', // DSL connections
        'cable', // Cable connections
        'fiber', // Fiber connections
        'mobileData', // Mobile data connections (e.g. 4G voor Thuis)

        // TV filters:
        'tvChannels', // Array with slugs of TV channels
        'tvExtraReceiver', // Extra TV receiver (true/false)
        'tvRecording', // Record TV shows (true/false)
        'tvRewind', // Rewinding (true/false)
        'tvApp', // TV app (true/false)
        'tvChannelCount', // Number of TV channels (int)
        'tvChannelCountHD', // Number of HD TV channels (int)

        // Phone options:
        'numberRetention', // Number retention (true/false)
        'phoneUnlimitedNL', // Unlimited calling in NL (true/false)
        'phoneUnlimitedEU', // Unlimited calling in Europe (true/false)
        'phoneUnlimitedWorld', // Unlimited calling world-wide (true/false)

        // Generic options:
        'products',

        // Tracking params (which should not be removed):
        'dci', // Daisycon click ID
        'utm_source', // UTM source
        'utm_medium', // UTM medium
        'utm_campaign', // UTM campaign
        'channel', // Channel
        'gclid', // Google click ID
        'msclkid', // Microsoft click ID
        'fbclid', // Facebook click ID
        'wbraid', // Google click ID
        'gbraid', // Google click ID
    ];
}

/**
 * Object containing custom data types for all filters. Not needed for strings
 */
export function filterDataTypes() {
    return {
        minSpeed: Number,
        minUpload: Number,
        tvChannelCount: Number,
        tvChannelCountHD: Number,
        products: Number,
        premium: Boolean,

        dsl: Boolean,
        cable: Boolean,
        fiber: Boolean,
        mobileData: Boolean,

        phoneUnlimitedNL: Boolean,
        phoneUnlimitedEU: Boolean,
        phoneUnlimitedWorld: Boolean,
        numberRetention: Boolean,
        tvExtraReceiver: Boolean,
        tvApp: Boolean,
        tvRecording: Boolean,
        tvRewind: Boolean,

        providers: Array,
        combiProviders: Array,
        combiProvidersEnergy: Array,
        tvChannels: Array,
    };
}

/**
 * Create an object with empty filter values (e.g., { minSpeed: null })
 * @returns {{}}
 */
export function filterPropertiesObject() {
    const propertiesObject = {};
    const dataTypes = filterDataTypes();

    filterProperties().forEach((entry) => {
        propertiesObject[entry] = null;

        if (Object.prototype.hasOwnProperty.call(dataTypes, entry)) {
            if (dataTypes[entry] === Boolean) {
                // Default to false instead of null for booleans:
                propertiesObject[entry] = false;
            } else if (dataTypes[entry] === Number) {
                // Default to false instead of null for booleans:
                propertiesObject[entry] = 0;
            } else if (dataTypes[entry] === Array) {
                // Default to empty array instead of null for arrays:
                propertiesObject[entry] = [];
            }
        }
    });

    return propertiesObject;
}

/**
 * Return an object with default filter values
 */
export function defaultFilterValues() {
    return {
        ...filterPropertiesObject(),
        // Only add properties below when you want a different default value than null:
        minSpeed: -1,
        productType: 'it',
        dsl: true,
        cable: true,
        fiber: true,
        mobileData: false,
        products: 10,
        sort: 'price',
        premium: false,
    };
}

/**
 * Return QuickFilter objects for this comparer
 */
export function quickFilters() {
    return [
        {
            icon: 'x-fa-tv-retro',
            label: 'TV-pakket',
            tooltip: 'Neem tv bij je internetabonnement',
            filters: {
                productType: 'it',
            },
            unset: ((filters) => {
                filters.productType = 'i';
            }),
            when: ((filters) => (filters.productType === 'i')),
        },
        {
            icon: 'x-fa-rabbit-fast',
            label: '100 Mb/s en meer',
            tooltip: 'Alle internetabonnementen met een snelheid van 100 MBit/s of hoger',
            filters: {
                minSpeed: 100,
            },
            unset: ((filters) => {
                filters.minSpeed = 50;
            }),
            when: ((filters, location) => (location == null || location.data == null || location.data.max_speed >= 100)),
        },
        {
            icon: 'football',
            label: 'ESPN Eredivisie 1',
            tooltip: 'ESPN Eredivisie 1 in je tv-pakket',
            set: ((filters) => {
                if (!Array.isArray(filters.tvChannels)) {
                    filters.tvChannels = [];
                }

                filters.tvChannels.push('fox-sports-eredivisie-1');
            }),
            unset: ((filters) => {
                // Remove FOX Sports from TV channel selection:
                const index = filters.tvChannels.indexOf('fox-sports-eredivisie-1');

                if (index > -1) {
                    filters.tvChannels.splice(index);
                }
            }),
            active: (filters) => Array.isArray(filters.tvChannels) && filters.tvChannels.includes('fox-sports-eredivisie-1'),
            when: ((filters) => (filters.productType === 'it' || filters.productType === 'ipt')),
        },
        {
            icon: 'football',
            label: 'ESPN Compleet',
            tooltip: 'ESPN Eredivisie 1, 2 & 3 in je tv-pakket',
            set: ((filters) => {
                if (!Array.isArray(filters.tvChannels)) {
                    filters.tvChannels = [];
                }

                filters.tvChannels.push('fox-sports-eredivisie-1');
                filters.tvChannels.push('fox-sports-eredivisie-3');
                filters.tvChannels.push('fox-sports-eredivisie-5');
            }),
            unset: ((filters) => {
                // Remove FOX Sports 1,2,3 from TV channel selection:
                let index = filters.tvChannels.indexOf('fox-sports-eredivisie-1');

                if (index > -1) {
                    filters.tvChannels.splice(index);
                }

                index = filters.tvChannels.indexOf('fox-sports-eredivisie-3');

                if (index > -1) {
                    filters.tvChannels.splice(index);
                }

                index = filters.tvChannels.indexOf('fox-sports-eredivisie-5');

                if (index > -1) {
                    filters.tvChannels.splice(index);
                }
            }),
            active: (filters) => Array.isArray(filters.tvChannels) && filters.tvChannels.includes('fox-sports-eredivisie-1') && filters.tvChannels.includes('fox-sports-eredivisie-3') && filters.tvChannels.includes('fox-sports-eredivisie-5'),
            when: ((filters) => (filters.productType === 'it' || filters.productType === 'ipt')),
        },
        {
            icon: 'x-fa-tachometer',
            label: 'Alleen glasvezel',
            tooltip: 'Alleen glasvezel internet tonen',
            filters: {
                dsl: false,
                cable: false,
                fiber: true,
            },
            unset: ((filters) => {
                filters.dsl = true;
                filters.cable = true;
            }),
            when: ((filters, location) => (location != null && location.data != null && location.data.max_fiber != null)),
        },
    ];
}

/**
 * Convert filter values to API filter parameters
 * @param filters
 */
export function filtersToApiFilters(filters) {
    const apiFilters = {
        min_download_speed_expected: filters.minSpeed,
        min_upload_speed: filters.minUpload,
        product_type: filters.productType,
        delivery_time: filters.deliveryTime,
        sort: filters.sort,
        premium: filters.premium,

        connection_type_dsl: filters.dsl,
        connection_type_cable: filters.cable,
        connection_type_fiber: filters.fiber,
        connection_type_4g: filters.mobileData,
        connection_type_5g: filters.mobileData,

        p_number_retention: filters.numberRetention,
        p_unlimited_nl: filters.phoneUnlimitedNL,
        p_unlimited_europe: filters.phoneUnlimitedEU,
        p_unlimited_world: filters.phoneUnlimitedWorld,

        tv_channel_count: filters.tvChannelCount,
        tv_channel_hd_count: filters.tvChannelCountHD,

        // Array filters:
        providers: filters.providers != null ? filters.providers.join(',') : null,
        tv_channels: filters.tvChannels != null ? JSON.stringify(filters.tvChannels) : null,
        tv_options: null, // <-- these are set below because it needs to be passed as a JSON encoded array with options

        items_per_page: filters.products,
    };

    let combiProviders = [];

    if (filters.combiProviders !== null && filters.combiProviders.length > 0) {
        combiProviders = filters.combiProviders;
    }
    if (filters.combiProvidersEnergy !== null && filters.combiProvidersEnergy.length > 0) {
        combiProviders = combiProviders.concat(filters.combiProvidersEnergy);
    }

    if (combiProviders.length > 0) {
        apiFilters.combi_providers = combiProviders.join(',');
    }

    // TV options:
    const tvOptions = [];

    if (filters.tvExtraReceiver === true) {
        tvOptions.push('t_extra_receiver');
    }
    if (filters.tvRecording === true) {
        tvOptions.push('t_record');
    }
    if (filters.tvRewind === true) {
        tvOptions.push('t_rewind');
    }
    if (filters.tvApp === true) {
        tvOptions.push('t_app');
    }

    if (tvOptions.length > 0) {
        apiFilters.tv_options = JSON.stringify(tvOptions);
    }

    // Add Meerclick tracking:
    if (typeof window.global_tracking !== 'undefined') {
        apiFilters.tracking = window.global_tracking;
    }

    return apiFilters;
}

export function readFiltersFromURL(query) {
    const filters = defaultFilterValues();
    const dataTypes = filterDataTypes();

    filterProperties().forEach((entry) => {
        if (typeof query[entry] !== 'undefined' && query[entry] !== null) {
            let value = query[entry];

            // Convert data type?
            if (Object.prototype.hasOwnProperty.call(dataTypes, entry)) {
                if (dataTypes[entry] === Boolean) {
                    value = value === true || value === 'true';
                } else if (dataTypes[entry] === Array && typeof value === 'string') {
                    value = value.split(',');

                    // Remove empty items from array:
                    value = value.filter((el) => el != null && el !== '');
                } else {
                    value = dataTypes[entry](value);
                }
            }

            filters[entry] = value;
        }
    });

    return filters;
}

export function filtersToUrlParams(filters) {
    if (filters == null || typeof filters === 'undefined') {
        return {};
    }

    const urlParams = {};
    const defaultFilters = defaultFilterValues();

    filterProperties().forEach((filterName) => {
        if (filters[filterName] !== null && typeof filters[filterName] !== 'undefined' && filters[filterName] !== defaultFilters[filterName]) {
            // Check for empty arrays:
            if (filters[filterName] !== null && Array.isArray(filters[filterName])) {
                if (filters[filterName].length === 0 && defaultFilters[filterName] == null) {
                    // Skip empty array:
                    return;
                }
            }

            urlParams[filterName] = String(filters[filterName]);
        }
    });

    return urlParams;
}
